/**
 * WorkspaceLayout — authenticated creator workspace shell.
 *
 * Desktop: fixed left sidebar + header + main content.
 * Mobile: slide-over panel for navigation.
 *
 * Semantic landmarks: <header>, <nav>, <main>, <aside>.
 * Full keyboard navigation with visible focus and ARIA labels.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, LogOut, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/hooks/auth';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useScrollLock } from '@/hooks/useScrollLock';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { Avatar } from '@/components/ui/Avatar';
import { WORKSPACE_NAV_ITEMS } from '@/config/workspace';
import { ROUTES } from '@/app/routes/paths';
import { NotificationCenter } from '@/components/notification';
import { BrandLogo } from '@/components/layout';

export function WorkspaceLayout(): JSX.Element {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const mobileNavRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useScrollLock(mobileNavOpen);
  useFocusTrap(mobileNavRef, mobileNavOpen);

  useEffect(() => {
    setMobileNavOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileNavOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [mobileNavOpen]);

  useEffect(() => {
    if (!userMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [userMenuOpen]);

  useEffect(() => {
    if (!userMenuOpen) return;
    const escapeHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setUserMenuOpen(false);
    };
    document.addEventListener('keydown', escapeHandler);
    return () => document.removeEventListener('keydown', escapeHandler);
  }, [userMenuOpen]);

  const handleSignOut = useCallback(async () => {
    await signOut();
    navigate(ROUTES.HOME);
  }, [signOut, navigate]);

  const handleKeyDown = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'flex flex-col gap-0.5 rounded-md px-3 py-2.5 text-sm transition-all duration-fast ease-standard',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/30 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
      isActive
        ? 'bg-primary-50 text-primary-800 font-medium'
        : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900',
    );

  const userName = user?.fullName || user?.email || 'Creator';
  const userInitials = userName.charAt(0).toUpperCase();

  return (
    <div className="flex min-h-screen bg-surface-secondary">
      {/* Skip link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[60] focus:rounded-md focus:bg-primary-700 focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-white"
      >
        Skip to main content
      </a>

      {/* Desktop Sidebar */}
      {isDesktop && (
        <aside
          className="fixed inset-y-0 left-0 z-30 flex w-64 flex-col border-r border-border bg-surface"
          aria-label="Workspace navigation"
        >
          <div className="flex h-16 items-center gap-2.5 border-b border-border px-6">
            <BrandLogo className="h-7 shrink-0" />
            <span className="text-sm font-semibold tracking-tight text-foreground">
              Creator Workspace
            </span>
          </div>
          <nav className="flex flex-1 flex-col gap-1 p-4" aria-label="Primary">
            {WORKSPACE_NAV_ITEMS.map((item) => (
              <NavLink key={item.path} to={item.path} end={item.path === '/workspace'} className={navLinkClass}>
                <span>{item.label}</span>
                <span className="text-xs text-muted-foreground">{item.description}</span>
              </NavLink>
            ))}
          </nav>
          <div className="border-t border-border p-4">
            <button
              onClick={handleSignOut}
              className={cn(
                'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-neutral-600',
                'transition-all duration-fast ease-standard hover:bg-neutral-100 hover:text-neutral-900',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/30',
              )}
            >
              <LogOut size={16} aria-hidden="true" />
              Sign Out
            </button>
          </div>
        </aside>
      )}

      {/* Mobile Slide-Over */}
      {!isDesktop && mobileNavOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Workspace navigation menu">
          <div
            className="absolute inset-0 bg-overlay transition-opacity duration-normal ease-standard"
            onClick={() => setMobileNavOpen(false)}
            aria-hidden="true"
          />
          <div
            ref={mobileNavRef}
            className="absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col bg-surface shadow-xl transition-transform duration-normal ease-standard"
          >
            <div className="flex h-16 items-center justify-between border-b border-border px-6">
              <div className="flex items-center gap-2.5">
                <BrandLogo className="h-7 shrink-0" />
                <span className="text-sm font-semibold tracking-tight text-foreground">Creator Workspace</span>
              </div>
              <button
                onClick={() => setMobileNavOpen(false)}
                aria-label="Close navigation menu"
                className="flex h-11 w-11 items-center justify-center rounded-md text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/30"
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col gap-1 overflow-y-auto p-4" aria-label="Primary">
              {WORKSPACE_NAV_ITEMS.map((item) => (
                <NavLink key={item.path} to={item.path} end={item.path === '/workspace'} className={navLinkClass}>
                  <span>{item.label}</span>
                  <span className="text-xs text-muted-foreground">{item.description}</span>
                </NavLink>
              ))}
            </nav>
            <div className="border-t border-border p-4">
              <button
                onClick={handleSignOut}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-neutral-600 transition-colors hover:bg-neutral-100 hover:text-neutral-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/30"
              >
                <LogOut size={16} aria-hidden="true" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className={cn('flex min-h-screen flex-1 flex-col', isDesktop && 'lg:pl-64')}>
        {/* Workspace Header */}
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-border bg-surface px-4 lg:px-8">
          <div className="flex items-center gap-3">
            {!isDesktop && (
              <button
                onClick={() => setMobileNavOpen(true)}
                aria-label="Open navigation menu"
                aria-expanded={mobileNavOpen}
                className="flex h-11 w-11 items-center justify-center rounded-md text-neutral-600 transition-colors hover:bg-neutral-100 hover:text-neutral-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/30"
              >
                <Menu size={20} aria-hidden="true" />
              </button>
            )}
            <span className="text-sm font-medium text-foreground">
              {WORKSPACE_NAV_ITEMS.find((n) => n.path === location.pathname)?.label ?? 'Overview'}
            </span>
          </div>

          {/* Notification Bell + User Menu */}
          <div className="flex items-center gap-2">
            <div className="relative">
              <NotificationCenter />
            </div>

            {/* User Menu */}
            <div ref={userMenuRef} className="relative">
              <button
                onClick={() => setUserMenuOpen((v) => !v)}
                onKeyDown={(e) => handleKeyDown(e, () => setUserMenuOpen((v) => !v))}
                aria-label="Open user menu"
                aria-expanded={userMenuOpen}
                aria-haspopup="menu"
                className="flex h-11 items-center gap-2 rounded-md py-1 pl-1 pr-2 transition-colors hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/30"
              >
                <Avatar name={userInitials} size="sm" />
                <span className="hidden text-sm font-medium text-foreground sm:inline">{userName}</span>
                <ChevronDown size={14} className="text-neutral-400" aria-hidden="true" />
              </button>
              {userMenuOpen && (
                <div
                  role="menu"
                  aria-label="User menu"
                  className="absolute right-0 top-12 w-56 rounded-lg border border-border bg-surface py-2 shadow-lg"
                >
                  <div className="px-3 py-2 border-b border-border-subtle">
                    <p className="text-sm font-medium text-foreground truncate">{userName}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                  <NavLink
                    to="/workspace/account"
                    role="menuitem"
                    className="block px-3 py-2 text-sm text-neutral-700 transition-colors hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/30"
                  >
                    Account Settings
                  </NavLink>
                  <button
                    onClick={handleSignOut}
                    role="menuitem"
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-neutral-700 transition-colors hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/30"
                  >
                    <LogOut size={14} aria-hidden="true" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main id="main-content" className="flex-1 px-4 py-6 lg:px-8 lg:py-8" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
